import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { trainingApi } from "../../services/trainingApi";
import { StatusBadge } from "../../components/StatusBadge";
import { PageHelpBanner } from "../../components/PageHelpBanner";
import { useLocalization } from "../../hooks/useLocalization";

export default function MyTrainingPage() {
  const navigate = useNavigate();
  const { t } = useLocalization();
  const [assignments, setAssignments] = useState([]);
  const [retraining, setRetraining] = useState([]);
  const [certifications, setCertifications] = useState([]);
  const [courseTitles, setCourseTitles] = useState({});

  useEffect(() => {
    trainingApi.myAssignments().then((r) => {
      setAssignments(r.data);
      r.data.forEach((a) => {
        trainingApi.getCourseForEmployee(a.course_id).then((res) => setCourseTitles((prev) => ({ ...prev, [a.course_id]: res.data.title_en }))).catch(() => {});
      });
    }).catch(() => {});
    trainingApi.myRetraining().then((r) => setRetraining(r.data)).catch(() => {});
    trainingApi.myCertifications().then((r) => setCertifications(r.data)).catch(() => {});
  }, []);

  const today = new Date().toISOString().slice(0, 10);
  const active = assignments.filter((a) => a.status !== "COMPLETED" && a.status !== "WAIVED" && a.status !== "NOT_APPLICABLE");
  const completed = assignments.filter((a) => a.status === "COMPLETED");
  const retrainingPending = retraining.filter((r) => !r.completed_at);

  return (
    <div className="space-y-5" data-testid="my-training-page">
      <PageHelpBanner pageKey="my-training" title={t("pageHelp.defaultTitle")}>
        <p>{t("pageHelp.myTraining")}</p>
      </PageHelpBanner>
      <div>
        <h2 className="text-xl font-bold tracking-tight text-gray-900">{t("myTraining.title")}</h2>
        <p className="text-sm text-gray-500">{t("myTraining.subtitle")}</p>
      </div>

      {retrainingPending.length > 0 && (
        <div className="bg-red-50 border border-red-200 rounded-xl p-4" data-testid="retraining-banner">
          <p className="text-sm font-semibold text-red-700">{t("myTraining.retrainingBannerTitle")}</p>
          <p className="text-xs text-red-600 mt-1">{t("myTraining.retrainingBannerBody", { count: retrainingPending.length })}</p>
        </div>
      )}

      <div className="space-y-3">
        <p className="text-xs uppercase tracking-[0.2em] text-gray-500">{t("myTraining.activeCourses")}</p>
        {active.map((a) => (
          <button key={a.id} data-testid={`my-assignment-row-${a.id}`} onClick={() => navigate(`/employee/training/courses/${a.course_id}`)}
            className="w-full text-left bg-white border border-gray-200 rounded-xl p-4 flex items-center justify-between hover:border-blue-400 transition-colors">
            <div>
              <p className="text-sm font-semibold text-gray-800">{courseTitles[a.course_id] || t("myTraining.courseAssignment")}</p>
              <p className="text-xs text-gray-500">{t("myTraining.due")}: {a.due_date || t("myTraining.noDeadline")}</p>
            </div>
            <StatusBadge status={a.status} />
          </button>
        ))}
        {active.length === 0 && <p className="text-sm text-gray-500" data-testid="no-active-assignments">{t("myTraining.noActive")}</p>}
      </div>

      <div className="space-y-3">
        <p className="text-xs uppercase tracking-[0.2em] text-gray-500">{t("myTraining.completed")}</p>
        {completed.map((a) => (
          <div key={a.id} className="bg-white border border-gray-200 rounded-xl p-4 flex items-center justify-between" data-testid={`completed-assignment-row-${a.id}`}>
            <p className="text-sm text-gray-700">{courseTitles[a.course_id] || t("myTraining.courseAssignment")}</p>
            <StatusBadge status={a.status} />
          </div>
        ))}
        {completed.length === 0 && <p className="text-sm text-gray-500" data-testid="no-completed-assignments">{t("myTraining.noCompleted")}</p>}
      </div>

      <div className="space-y-3">
        <p className="text-xs uppercase tracking-[0.2em] text-gray-500">{t("myTraining.myCertifications")}</p>
        {certifications.map((c) => (
          <div key={c.id} className="bg-white border border-gray-200 rounded-xl p-4 flex items-center justify-between" data-testid={`my-cert-row-${c.id}`}>
            <div>
              <p className="text-sm font-semibold text-gray-800">{c.certification_key}</p>
              <p className="text-xs text-gray-500">{t("myTraining.expires")}: {c.expires_at ? c.expires_at.slice(0, 10) : t("myTraining.never")}</p>
            </div>
            <StatusBadge status={c.expires_at && c.expires_at.slice(0, 10) <= today ? "EXPIRED" : c.status} />
          </div>
        ))}
        {certifications.length === 0 && <p className="text-sm text-gray-500" data-testid="no-my-certs">{t("myTraining.noCertifications")}</p>}
      </div>
    </div>
  );
}
